import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken } from "@/hooks";
import { UsersApi } from "@/openapi/generated";
import type { CreateUserRequest } from "@/openapi/generated/models";
import { QueryKeys } from "@/lib/types";
import { createAuthenticatedApi, getApiErrorMessage } from "@/lib/utils";

export const useParentCreate = (
  toast: ReturnType<typeof import("@/components/feedback").useToast>,
) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();

  const { mutate, isPending, isSuccess, isError } = useMutation({
    mutationFn: async (createUserRequest: CreateUserRequest) => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }

      // Build a clean payload with no duplicate or extra keys (avoids 400 from strict parsers)
      const cleanRequest: CreateUserRequest = {
        email: createUserRequest.email,
        password: createUserRequest.password,
        firstName: createUserRequest.firstName,
        lastName: createUserRequest.lastName,
        cellPhone: createUserRequest.cellPhone,
        homePhone: createUserRequest.homePhone ?? null,
        address: createUserRequest.address ?? null,
        city: createUserRequest.city ?? null,
        state: createUserRequest.state ?? null,
        zip: createUserRequest.zip ?? null,
        country: createUserRequest.country ?? null,
        leadSource: createUserRequest.leadSource ?? null,
        students:
          createUserRequest.students?.map((s) => ({
            selfEnrollment: s.selfEnrollment,
            firstName: s.firstName,
            lastName: s.lastName,
            dateOfBirth: s.dateOfBirth,
            gender: s.gender,
            locationIds: s.locationIds,
          })) ?? null,
        locationIds: createUserRequest.locationIds ?? undefined,
      };

      const service = createAuthenticatedApi(UsersApi, accessToken);

      return service.apiUserRegisterMemberPost({
        createUserRequest: cleanRequest,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        predicate: (query) => {
          return (
            Array.isArray(query.queryKey) &&
            (query.queryKey[0] === QueryKeys.UsersScrollGet ||
              query.queryKey[0] === "users-scroll-get" ||
              query.queryKey[0] === "users")
          );
        },
      });
      toast({
        title: "Parent created",
        description: "Parent created successfully!",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error: any) => {
      const errorMessage = await getApiErrorMessage(error, "Failed to create parent");

      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });

  return { mutate, isPending, isError, isSuccess };
};

